import React, { useState, useMemo, useEffect } from 'react';
import { EventCard } from '../types';
import EarningsEventCard from './EarningsEventCard';
import './EarningsCalendar.css';
import EarningsWeekHeader from './EarningsWeekHeader';

interface EarningsCalendarProps {
  events: EventCard[];
  onEventClick: (event: EventCard) => void;
  customDayLabels?: string[]; // Optional: 5 custom labels for the weekdays
}

interface GroupedEvents {
  [dateKey: string]: {
    pre: EventCard[];
    post: EventCard[];
  };
}

// Custom viewport hook for mobile detection
function useIsMobile(breakpoint: number = 600) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const EarningsCalendar: React.FC<EarningsCalendarProps> = ({ events, onEventClick, customDayLabels }) => {
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    // Start with the current week (Monday)
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day + (day === 0 ? -6 : 1); // Adjust to Monday
    return new Date(today.setDate(diff));
  });

  const isMobile = useIsMobile();
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(() => {
    // Initialize to today if in current week, otherwise default to 0 (Monday)
    const today = new Date();
    const day = today.getDay();
    const mondayIndex = day === 0 ? -6 : 1;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - day + mondayIndex);
    currentMonday.setHours(0, 0, 0, 0);

    const weekStart = new Date(currentWeekStart);
    weekStart.setHours(0, 0, 0, 0);

    if (currentMonday.getTime() === weekStart.getTime()) {
      // We're in the current week, return today's index (0-4 for Mon-Fri)
      return day === 0 ? 4 : day - 1; // Sunday maps to Friday (4), else day - 1
    }
    return 0; // Default to Monday
  });

  // Group events by date and market timing (pre/post)
  const groupedEvents = useMemo<GroupedEvents>(() => {
    const grouped: GroupedEvents = {};

    events.forEach((event) => {
      if (!event.ends_at) return;

      const eventDate = new Date(event.ends_at);
      const dateKey = eventDate.toISOString().split('T')[0]; // YYYY-MM-DD

      if (!grouped[dateKey]) {
        grouped[dateKey] = { pre: [], post: [] };
      }

      // Determine if it's pre-market or post-market
      // Check title or description for keywords
      const title = event.title.toLowerCase();
      const isPreMarket = title.includes('pre market') || title.includes('premarket');
      const isPostMarket = title.includes('post market') || title.includes('postmarket');

      if (isPostMarket) {
        grouped[dateKey].post.push(event);
      } else if (isPreMarket) {
        grouped[dateKey].pre.push(event);
      } else {
        // Default to pre-market if not specified
        grouped[dateKey].pre.push(event);
      }
    });

    return grouped;
  }, [events]);

  // Generate array of 5 weekdays starting from currentWeekStart
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  }, [currentWeekStart]);

  // Navigate to previous week (desktop)
  const goToPreviousWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newDate);
  };

  // Navigate to next week (desktop)
  const goToNextWeek = () => {
    const newDate = new Date(currentWeekStart);
    newDate.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newDate);
  };

  // Navigate to previous day (mobile)
  const goToPreviousDay = () => {
    if (selectedDayIndex > 0) {
      // Move to previous day in same week
      setSelectedDayIndex(selectedDayIndex - 1);
    } else {
      // Currently on Monday (index 0), jump to previous Friday
      const newDate = new Date(currentWeekStart);
      newDate.setDate(currentWeekStart.getDate() - 3); // Go back 3 days to previous Friday
      setCurrentWeekStart(newDate);
      setSelectedDayIndex(4); // Set to Friday
    }
  };

  // Navigate to next day (mobile)
  const goToNextDay = () => {
    if (selectedDayIndex < 4) {
      // Move to next day in same week
      setSelectedDayIndex(selectedDayIndex + 1);
    } else {
      // Currently on Friday (index 4), jump to next Monday
      const newDate = new Date(currentWeekStart);
      newDate.setDate(currentWeekStart.getDate() + 3); // Go forward 3 days to next Monday
      setCurrentWeekStart(newDate);
      setSelectedDayIndex(0); // Set to Monday
    }
  };

  // Get month and year for calendar title
  const getMonthYear = () => {
    return currentWeekStart.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  // Check if a date is today
  const isToday = (date: Date) => {
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  // Check if a date is in the past
  const isPast = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(date);
    compareDate.setHours(0, 0, 0, 0);
    return compareDate < today;
  };

  // Get events for a specific date
  const getEventsForDate = (date: Date, timing: 'pre' | 'post'): EventCard[] => {
    const dateKey = date.toISOString().split('T')[0];
    return groupedEvents[dateKey]?.[timing] || [];
  };

  return (
    <div className="earnings-calendar-container">
      {/* Calendar Headings above component */}
      <div className="earnings-calendar-header">
        <div className="earnings-calendar-title">
          <h1>{getMonthYear()}</h1>
          <p>Earnings Calendar</p>
        </div>
      </div>

      <div className="calendar-surface">
        {/* Week Header Component */}
        <EarningsWeekHeader
          weekDays={weekDays}
          isToday={isToday}
          onPrev={isMobile ? goToPreviousDay : goToPreviousWeek}
          onNext={isMobile ? goToNextDay : goToNextWeek}
          customDayLabels={customDayLabels}
          mobile={isMobile}
          selectedDayIndex={selectedDayIndex}
        />

      {/* Pre Market Section (no label) */}
      <div className="earnings-market-section tight">
        <div className="earnings-grid">
          {weekDays.map((date, index) => {
            const preMarketEvents = getEventsForDate(date, 'pre');
            const today = isToday(date);
            const past = isPast(date);
            const isSelected = isMobile ? index === selectedDayIndex : false;
            const dayLabel = date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric' });
            return (
              <div
                key={`pre-${index}`}
                className={`earnings-day-column ${today ? 'is-today' : ''} ${past ? 'is-past' : ''} ${isSelected ? 'is-selected' : ''}`}
                data-day-label={dayLabel}
              >
                {preMarketEvents.length > 0 && (
                  <div className="earnings-market-group">
                    {preMarketEvents.map((event, eventIndex) => (
                      <EarningsEventCard
                        key={`pre-${index}-${eventIndex}`}
                        event={event}
                        onCardClick={onEventClick}
                        marketTiming="pre"
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      </div>
    </div>
  );
};

export default EarningsCalendar;
